import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { GlobalLoader } from '@/shared/ui';

import { GlobalLoaderContext } from './globalLoaderContext';

interface GlobalLoaderProviderProps {
  children: ReactNode;
}

export const GlobalLoaderProvider = ({ children }: GlobalLoaderProviderProps) => {
  const [activeKeys, setActiveKeys] = useState(() => new Map<string, string | undefined>());

  const showLoader = useCallback((key: string, message?: string) => {
    setActiveKeys(prev => {
      const next = new Map(prev);
      next.set(key, message);
      return next;
    });
  }, []);

  const hideLoader = useCallback((key: string) => {
    setActiveKeys(prev => {
      if (!prev.has(key)) return prev;

      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const isLoading = activeKeys.size > 0;
  const message = isLoading ? [...activeKeys.values()].at(-1) : undefined;

  const value = useMemo(
    () => ({ showLoader, hideLoader, isLoading, message }),
    [hideLoader, isLoading, message, showLoader]
  );

  return (
    <GlobalLoaderContext value={value}>
      {children}
      <GlobalLoader
        open={isLoading}
        message={message}
      />
    </GlobalLoaderContext>
  );
};
